import {
  queriesBackgroundCheckConfigurations,
  queriesInsuranceMonitorConfigurations,
  queriesSharedFileRequestConfigurations
} from "@query";
import {
  RequirementType,
  SharedFileRequestConfiguration,
  SharedFileRequestConfigurationsApiCreateSharedFileRequestConfigurationRequest,
  SharedFileRequestContextType
} from "@wingspanhq/wingspan-api-sdk";

import { cleanOptionalFormValues } from "../../../shared/utils/cleanOptionalFormValues";
import { RequirementDefinitionTemplate } from "../routes/RouteCreateRequirementByTemplate/types";
import {
  BaseDataSourceResponse,
  RequirementDefinitionFormValues
} from "../types";
import { baseMapFormDataToRequirementCreateRequest } from "../utils/baseMapFormDataToRequirementCreateRequest";

/**
 * v3 permits exactly **one shared-file request configuration per `contextType`**
 * per account and answers `409 ResourceConflict` on a second create — where v1's
 * `POST /shared-files/settings` returned the already-existing row instead. The
 * configuration also outlives the definition that points at it, so once any of
 * the four smart document-verification requirements has ever been created,
 * deleting the definition frees its picker tile but leaves the configuration
 * behind and every later create conflicts. Confirmed with the API team.
 *
 * So restore v1's get-or-create: for a pinned context, look the configuration up
 * and reuse its id. Safe because these templates derive the whole configuration
 * (title, instructions, accepted mime types, context) from the template itself —
 * an existing row for the context is the row we would have created.
 *
 * `None` is deliberately exempt: the standard file-upload template carries a
 * caller-supplied title and mime types and mints a fresh configuration per
 * requirement.
 */
const fetchReusableSharedFileConfiguration = async (
  createDataSourceRequest: unknown
): Promise<SharedFileRequestConfiguration | undefined> => {
  const contextType = (
    createDataSourceRequest as
      | SharedFileRequestConfigurationsApiCreateSharedFileRequestConfigurationRequest
      | undefined
  )?.sharedFileRequestConfigurationCreate?.contextType;

  if (!contextType || contextType === SharedFileRequestContextType.NONE) {
    return undefined;
  }

  try {
    const configurations =
      await queriesSharedFileRequestConfigurations.listSharedFileRequestConfigurations.forceFetchQueryData(
        { filter: { contextType: { eq: contextType } }, page: { size: 1 } }
      );

    return configurations.data?.[0];
  } catch {
    // The lookup is an optimization over the create, not a gate on it — fall
    // through and let the create answer. Its error is the one the caller already
    // renders (`dataSourceError`), so a failed lookup can never strand the UI
    // with no feedback.
    return undefined;
  }
};

// The shared-file create mints a v3 shared-file-request configuration, the
// insurance create a v3 monitor configuration and the background-check create a
// v3 background-check configuration — whose id is the only way to obtain a
// `dataSourceId` for a definition of that type. Signature has no branch here at
// all: its document template is minted during the file upload in
// `CreateSignaturePartialForm`, so it takes the
// `mapFormDataToRequirementCreateRequest` path below.
export const useCreateRequirementDefinitionSettings = () => {
  const [createSharedFileSettings, createSharedFileSettingsMeta] =
    queriesSharedFileRequestConfigurations.createSharedFileRequestConfiguration.useMutation();
  const [createMonitorSettings, createMonitorSettingsMeta] =
    queriesInsuranceMonitorConfigurations.createInsuranceMonitorConfiguration.useMutation();
  const [createBackgroundCheckSettings, createBackgroundCheckSettingsMeta] =
    queriesBackgroundCheckConfigurations.createBackgroundCheckConfiguration.useMutation();

  const submit = async ({
    template,
    formData
  }: {
    formData: RequirementDefinitionFormValues;
    template: RequirementDefinitionTemplate;
  }) => {
    const formValues = cleanOptionalFormValues(formData, {
      ignore: ["coverages"]
    });

    const {
      mapFormDataToDataSourceCreateRequest,
      mapDataSourceResponseToRequirementCreateRequest,
      mapFormDataToRequirementCreateRequest
    } = template.form || {};

    const { requirementType } = template;

    const createDataSourceRequest = mapFormDataToDataSourceCreateRequest?.(
      formValues,
      template
    );

    const reusableSharedFileConfiguration =
      requirementType === RequirementType.DOCUMENT_UPLOAD
        ? await fetchReusableSharedFileConfiguration(createDataSourceRequest)
        : undefined;

    if (
      reusableSharedFileConfiguration &&
      mapDataSourceResponseToRequirementCreateRequest
    ) {
      return {
        ...mapDataSourceResponseToRequirementCreateRequest(
          reusableSharedFileConfiguration,
          template
        ),
        ...baseMapFormDataToRequirementCreateRequest(formValues)
      };
    }

    const createSourceData =
      requirementType === RequirementType.DOCUMENT_UPLOAD
        ? createSharedFileSettings
        : requirementType === RequirementType.INSURANCE_COVERAGE
          ? createMonitorSettings
          : requirementType === RequirementType.BACKGROUND_CHECK
            ? createBackgroundCheckSettings
            : null;

    return new Promise<
      Partial<BaseDataSourceResponse> &
        ReturnType<typeof baseMapFormDataToRequirementCreateRequest>
    >((resolve, reject) => {
      if (createSourceData && mapDataSourceResponseToRequirementCreateRequest) {
        createSourceData?.(createDataSourceRequest, {
          onError(error: Error) {
            reject(error);
          },
          onSuccess(response) {
            resolve({
              ...mapDataSourceResponseToRequirementCreateRequest(
                response,
                template
              ),
              ...baseMapFormDataToRequirementCreateRequest(formValues)
            });
          }
        });
      } else {
        resolve({
          ...mapFormDataToRequirementCreateRequest?.(formValues, template),
          ...baseMapFormDataToRequirementCreateRequest(formValues)
        });
      }
    });
  };

  return {
    // every data-source create is v3, so their errors are SDK ApiErrors, which
    // ErrorAlert resolves through the `errorMap` context it is given.
    dataSourceError:
      createSharedFileSettingsMeta.error ||
      createMonitorSettingsMeta.error ||
      createBackgroundCheckSettingsMeta.error,
    isLoading:
      createSharedFileSettingsMeta.isLoading ||
      createMonitorSettingsMeta.isLoading ||
      createBackgroundCheckSettingsMeta.isLoading,
    submit
  };
};
